import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { EditOutlined, MoreHoriz } from '@mui/icons-material';
import { DarkGreen } from '../../../../app/theme/tokens/colors';

export type BasketTableColumn<T> = {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  render: (row: T) => ReactNode;
};

export type BasketRowAction<T> = {
  label: string;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: (row: T) => boolean;
  onClick: (row: T) => void;
};

type BasketTableProps<T> = {
  columns: BasketTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string | number;
  /** Nome da linha, usado nos rótulos de acessibilidade das ações. */
  getRowLabel: (row: T) => string;
  /** Plural do que está listado, ex.: "perguntas". Usado no rodapé. */
  itemsLabel: string;
  emptyMessage: string;
  /** Vazio por causa da busca (e não por falta de cadastro). */
  searching?: boolean;
  onEdit?: (row: T) => void;
  editLabel?: string;
  menuActions?: BasketRowAction<T>[];
  /** Muda quando o conjunto filtrado muda; volta a tabela para a primeira página. */
  resetKey?: string;
  /** Linha destacada: a tabela abre a página dela e rola até ela. */
  highlightRowId?: string | number;
};

type RowMenuProps<T> = {
  row: T;
  label: string;
  actions: BasketRowAction<T>[];
};

function RowMenu<T>({ row, label, actions }: RowMenuProps<T>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title="Mais ações">
        <IconButton
          size="small"
          aria-label={`Mais ações de ${label}`}
          aria-haspopup="menu"
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <MoreHoriz fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            disabled={action.disabled?.(row)}
            onClick={() => {
              setAnchor(null);
              action.onClick(row);
            }}
            sx={action.destructive ? { color: '#d32f2f' } : undefined}
          >
            {action.icon && (
              <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                {action.icon}
              </ListItemIcon>
            )}
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Tabela das abas de Balaios, no padrão das tabelas da tela de Configuração. */
export function BasketTable<T>({
  columns,
  rows,
  getRowId,
  getRowLabel,
  itemsLabel,
  emptyMessage,
  searching = false,
  onEdit,
  editLabel = 'Editar',
  menuActions = [],
  resetKey,
  highlightRowId,
}: BasketTableProps<T>) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const hasActions = Boolean(onEdit) || menuActions.length > 0;

  useEffect(() => {
    setPage(0);
  }, [resetKey]);

  const highlightIndex =
    highlightRowId === undefined
      ? -1
      : rows.findIndex((row) => getRowId(row) === highlightRowId);
  const highlightedRef = useRef<HTMLTableRowElement | null>(null);

  // Declarado depois do reset da busca, para a página do destaque prevalecer.
  useEffect(() => {
    if (highlightIndex >= 0) {
      setPage(Math.floor(highlightIndex / rowsPerPage));
    }
    // Só reage a um novo destaque ou a uma mudança no tamanho da página.
  }, [highlightRowId, rowsPerPage]);

  useEffect(() => {
    highlightedRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [highlightRowId, page]);

  // Uma exclusão pode esvaziar a última página.
  const lastPage = Math.max(Math.ceil(rows.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, lastPage);
  const visibleRows = rows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          // Rola por dentro, com o cabeçalho fixo; o desconto cobre o topo do portal,
          // o título da tela, as abas, a busca e a paginação.
          maxHeight: 'calc(100vh - 420px)',
          minHeight: 240,
          overflow: 'auto',
          border: '1px solid #e6e6e6',
          borderRadius: 1,
        }}
      >
        <Table stickyHeader size="small" aria-label={itemsLabel}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    backgroundColor: DarkGreen,
                    color: 'white',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: DarkGreen,
                    color: 'white',
                    fontWeight: 700,
                    width: 110,
                  }}
                >
                  Ações
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row) => {
              const label = getRowLabel(row);
              const highlighted =
                highlightRowId !== undefined && getRowId(row) === highlightRowId;
              return (
                <TableRow
                  hover
                  key={getRowId(row)}
                  ref={highlighted ? highlightedRef : undefined}
                  aria-current={highlighted ? 'true' : undefined}
                  sx={
                    highlighted
                      ? {
                          '&, &:hover': { backgroundColor: '#e3f2dc' },
                          boxShadow: 'inset 4px 0 0 #318300',
                        }
                      : undefined
                  }
                >
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align}>
                      {column.render(row)}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {onEdit && (
                        <Tooltip title={editLabel}>
                          <IconButton
                            size="small"
                            aria-label={`${editLabel} ${label}`}
                            onClick={() => onEdit(row)}
                          >
                            <EditOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {menuActions.length > 0 && (
                        <RowMenu row={row} label={label} actions={menuActions} />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (hasActions ? 1 : 0)}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <Typography color="text.secondary">
                    {searching ? 'Nenhum resultado para a busca.' : emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={currentPage}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) =>
          count === 0 ? `Exibindo 0 ${itemsLabel}` : `Exibindo ${from}–${to} de ${count} ${itemsLabel}`
        }
        sx={{ flexShrink: 0, overflow: 'hidden' }}
      />
    </div>
  );
}
